import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, RefreshCw, Search, ChevronDown, Archive, ArchiveRestore, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { BillingPanel, PaymentPill, PricingPill, SendPaymentButton } from "@/components/BillingPanel";
import {
  listLeads,
  updateLeadStatus,
  archiveLead,
  restoreLead,
  binLead,
  unbinLead,
  deleteLead,
  clearAdminToken,
} from "@/lib/api";

const STATUSES = ["new", "contacted", "scheduled", "completed", "archived"];
const STATUS_COLOR = {
  new: { bg: "rgba(212,175,55,0.14)", color: "#8A6D17", border: "rgba(212,175,55,0.4)" },
  contacted: { bg: "rgba(11,37,69,0.08)", color: "#0B2545", border: "rgba(11,37,69,0.25)" },
  scheduled: { bg: "rgba(16,185,129,0.12)", color: "#065F46", border: "rgba(16,185,129,0.35)" },
  completed: { bg: "rgba(100,116,139,0.12)", color: "#334155", border: "rgba(100,116,139,0.3)" },
  archived: { bg: "rgba(148,163,184,0.18)", color: "#475569", border: "rgba(148,163,184,0.4)" },
};
const URGENCY_COLOR = {
  Standard: { bg: "rgba(11,37,69,0.07)", color: "#0B2545" },
  "Same Day": { bg: "rgba(212,175,55,0.18)", color: "#7A5E12" },
  Immediate: { bg: "rgba(220,38,38,0.1)", color: "#991B1B" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unpaid", label: "Unpaid" },
  { id: "paid", label: "Paid" },
  { id: "today", label: "Today's appointments" },
  { id: "awaiting", label: "Awaiting payment" },
  { id: "balance", label: "Balance due" },
  { id: "in_progress", label: "In Progress" },
  { id: "cancelled", label: "Cancelled", redirectTab: "refunds" },
  { id: "archived", label: "Archived" },
  { id: "bin", label: "Bin" },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const applyFilter = (lead, filterId) => {
  // Binned records ONLY show in the bin filter; archived ONLY in archive filter.
  if (filterId !== "bin" && lead.is_binned) return false;
  if (filterId !== "archived" && !lead.is_binned && lead.is_archived) return false;
  const ps = lead.payment_status || "unpaid";
  const opStatus = (lead.status || "new").toLowerCase();
  switch (filterId) {
    case "unpaid": return ps === "unpaid";
    case "paid": return ps === "paid";
    case "today": return lead.preferred_date === todayISO();
    case "awaiting": return ps === "payment_link_sent";
    case "balance": return ps === "remaining_balance" || ps === "partial_payment";
    case "in_progress": return opStatus === "in_progress" || opStatus === "scheduled";
    case "cancelled": return opStatus === "cancelled" || ps === "cancelled";
    case "archived": return !!lead.is_archived && !lead.is_binned;
    case "bin": return !!lead.is_binned;
    case "all":
    default: return true;
  }
};

export default function ConsumerLeadsPanel({ onSessionExpired, onNavigate }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // Honor ?status= query param so deep links like /admin/leads?status=in_progress
  // pre-filter the table.
  const [filterId, setFilterId] = useState(() => {
    if (typeof window === "undefined") return "all";
    const s = new URLSearchParams(window.location.search).get("status");
    if (!s) return "all";
    const known = FILTERS.find((f) => f.id === s.toLowerCase());
    return known ? known.id : "all";
  });
  const [active, setActive] = useState(null);

  // Filter click handler — if the filter is configured to redirect to another
  // tab (e.g. Cancelled → Refund Center), do that instead of setting state.
  const selectFilter = (f) => {
    if (f.redirectTab) {
      if (onNavigate) onNavigate(f.redirectTab);
      else navigate(`/admin?tab=${f.redirectTab}`);
      return;
    }
    setFilterId(f.id);
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await listLeads();
      setLeads(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        onSessionExpired?.();
        toast.error("Your session expired. Please sign in again.");
        return;
      }
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads
      .filter((l) => applyFilter(l, filterId))
      .filter((l) =>
        !q ||
        [l.full_name, l.email, l.phone, l.company, (l.services || []).join(" "), l.urgency, l.status, l.payment_status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
  }, [leads, query, filterId]);

  const stats = useMemo(() => {
    const active = leads.filter((l) => !l.is_archived && !l.is_binned);
    const total = active.length;
    const newCount = active.filter((l) => l.status === "new").length;
    const immediate = active.filter((l) => l.urgency === "Immediate").length;
    const unpaid = active.filter((l) =>
      ["unpaid", "payment_link_sent", "remaining_balance", "partial_payment"].includes(l.payment_status || "unpaid"),
    ).length;
    return { total, newCount, immediate, unpaid };
  }, [leads]);

  const filterCounts = useMemo(() => {
    const counts = {};
    for (const f of FILTERS) counts[f.id] = leads.filter((l) => applyFilter(l, f.id)).length;
    return counts;
  }, [leads]);

  const handleLeadUpdate = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    if (active?.id === updated.id) setActive(updated);
  };

  const onChangeStatus = async (id, status) => {
    try {
      const updated = await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (active?.id === id) setActive(updated);
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const onArchive = async (id) => {
    if (!window.confirm("Move this lead off the active board into Archived? You can restore it anytime.")) return;
    try {
      const updated = await archiveLead(id);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (active?.id === id) setActive(updated);
      toast.success("Lead archived.");
    } catch {
      toast.error("Failed to archive.");
    }
  };

  const onRestore = async (id) => {
    try {
      const updated = await restoreLead(id);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (active?.id === id) setActive(updated);
      toast.success("Lead restored.");
    } catch {
      toast.error("Failed to restore.");
    }
  };

  const onMoveToBin = async (id) => {
    if (!window.confirm("Move this lead to the Bin? You can still restore it from the Bin.")) return;
    try {
      const updated = await binLead(id);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (active?.id === id) setActive(updated);
      toast.success("Lead moved to Bin.");
    } catch {
      toast.error("Failed to move to bin.");
    }
  };

  const onRestoreFromBin = async (id) => {
    try {
      const updated = await unbinLead(id);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (active?.id === id) setActive(updated);
      toast.success("Lead restored from Bin.");
    } catch {
      toast.error("Failed to restore from bin.");
    }
  };

  const onPermanentDelete = async (id) => {
    if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
    try {
      await deleteLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Lead permanently deleted.");
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid="consumer-leads-panel">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#D4AF37" }}>
            Consumer Pipeline
          </div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#0B2545" }}>
            Service requests
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#475569" }}>
            Submissions from the public service request form.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchLeads}
          className="h-10"
          style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
          data-testid="consumer-refresh-button"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total leads" value={stats.total} accent="#0B2545" testId="stat-total" />
        <StatCard label="New" value={stats.newCount} accent="#D4AF37" testId="stat-new" />
        <StatCard label="Immediate urgency" value={stats.immediate} accent="#B91C1C" testId="stat-immediate" />
        <StatCard label="Unpaid / pending" value={stats.unpaid} accent="#7A5E12" testId="stat-unpaid" />
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="filter-chips">
        {FILTERS.map((f) => {
          const isActive = filterId === f.id;
          const isRedirect = !!f.redirectTab;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => selectFilter(f)}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full border text-xs font-semibold transition-all"
              style={{
                borderColor: isActive ? "#0B2545" : (isRedirect ? "rgba(212,175,55,0.5)" : "#E2E8F0"),
                background: isActive ? "#0B2545" : "#FFFFFF",
                color: isActive ? "#FFFFFF" : (isRedirect ? "#7A5E12" : "#0B2545"),
              }}
              data-testid={`filter-${f.id}`}
              title={isRedirect ? "Opens the Refund Center" : undefined}
            >
              {f.label}
              {isRedirect ? (
                <Undo2 className="h-3 w-3 -mr-0.5" style={{ color: "#7A5E12" }} />
              ) : (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold"
                  style={{
                    background: isActive ? "rgba(255,255,255,0.18)" : "#F1F5F9",
                    color: isActive ? "#FFFFFF" : "#475569",
                  }}
                >
                  {filterCounts[f.id] ?? 0}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, service..."
            className="h-10 pl-9"
            data-testid="search-input"
          />
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }}>
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-16" style={{ color: "#475569" }}>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading leads...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 sm:py-20 text-center" data-testid="consumer-empty-state">
            <div
              className="mx-auto mb-5 grid place-items-center h-14 w-14 rounded-2xl"
              style={{ background: "rgba(212,175,55,0.14)", color: "#7A5E12" }}
            >
              <Search className="h-7 w-7" />
            </div>
            <div className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>
              {query ? "No leads match your search." : "No leads currently in this status"}
            </div>
            <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "#475569" }}>
              {query
                ? "Try a different name, email, or service keyword."
                : "Once new leads arrive or change status, you'll see them here."}
            </p>
            <div className="mt-5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFilterId("all"); setQuery(""); }}
                style={{ borderColor: "#0B2545", color: "#0B2545" }}
                data-testid="consumer-empty-back"
              >
                Return to Leads
              </Button>
            </div>
          </div>
        ) : (
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow style={{ background: "#F8FAFC" }}>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Received</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Name</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Contact</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Services</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Urgency</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Payment</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} className="hover:bg-[#F8FAFC]" data-testid={`lead-row-${l.id}`}>
                  <TableCell className="text-xs" style={{ color: "#475569" }}>
                    {l.created_at ? format(new Date(l.created_at), "PP p") : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color: "#0B2545" }}>{l.full_name}</span>
                      {l.is_binned ? (
                        <span
                          className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                          style={{ background: "rgba(220,38,38,0.08)", color: "#991B1B", borderColor: "rgba(220,38,38,0.3)" }}
                          data-testid={`consumer-bin-tag-${l.id}`}
                        >
                          Bin
                        </span>
                      ) : l.is_archived ? (
                        <span
                          className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                          style={{ background: "#F8FAFC", color: "#475569", borderColor: "#E2E8F0" }}
                          data-testid={`consumer-archived-tag-${l.id}`}
                        >
                          Archived
                        </span>
                      ) : null}
                    </div>
                    {l.company ? <div className="text-xs" style={{ color: "#475569" }}>{l.company}</div> : null}
                    <div className="mt-1"><PricingPill value={l.pricing_type || "Standard"} /></div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs" style={{ color: "#0F172A" }}>{l.email}</div>
                    <div className="text-xs" style={{ color: "#475569" }}>{l.phone}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(l.services || []).map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px] font-medium" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}>{s}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell><UrgencyPill value={l.urgency} /></TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <PaymentPill value={l.payment_status || "unpaid"} />
                      {l.amount_due != null && l.payment_status !== "paid" ? (
                        <span className="text-[10px]" style={{ color: "#475569" }}>
                          Due: ${Number(l.amount_due).toFixed(2)}
                          {l.amount_paid > 0 ? ` · Paid: $${Number(l.amount_paid).toFixed(2)}` : ""}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell><StatusDropdown current={l.status} onSelect={(s) => onChangeStatus(l.id, s)} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-xs" style={{ color: "#0B2545" }} onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`view-lead-${l.id}`}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className={[
            "p-0 gap-0 flex flex-col overflow-hidden border-0 sm:border",
            // Mobile: full-screen drawer
            "w-screen h-[100dvh] max-w-none max-h-none rounded-none",
            // Tablet & desktop: centered modal with comfortable max width
            "sm:w-[95vw] sm:h-auto sm:max-w-[920px] sm:max-h-[90vh] sm:rounded-lg",
          ].join(" ")}
          data-testid="lead-detail-dialog"
        >
          {active ? (
            <>
              {/* Sticky header */}
              <div
                className="shrink-0 px-5 sm:px-7 py-4 border-b bg-white"
                style={{ borderColor: "#E2E8F0" }}
                data-testid="lead-detail-header"
              >
                <DialogHeader>
                  <DialogTitle className="font-display text-xl sm:text-2xl pr-8" style={{ color: "#0B2545" }}>
                    {active.full_name}
                    {active.request_id ? (
                      <span className="ml-2 inline-block text-[11px] font-mono px-1.5 py-0.5 rounded align-middle"
                            style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                        {active.request_id}
                      </span>
                    ) : null}
                  </DialogTitle>
                  <DialogDescription>
                    Submitted {active.created_at ? format(new Date(active.created_at), "PPpp") : "—"}
                  </DialogDescription>
                </DialogHeader>
              </div>

              {/* Scrollable body */}
              <div
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 sm:px-7 py-5 space-y-6"
                data-testid="lead-detail-body"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Detail label="Email" value={active.email} />
                  <Detail label="Phone" value={active.phone} />
                  <Detail label="Company" value={active.company || "—"} />
                  <Detail label="Urgency" value={active.urgency} />
                  <Detail label="Preferred date" value={active.preferred_date || "—"} />
                  <Detail label="Preferred time" value={active.preferred_time || "—"} />
                  <div className="sm:col-span-2">
                    <Detail label="Service address" value={active.service_address || "—"} />
                  </div>
                  <div className="sm:col-span-2">
                    <Detail
                      label="Services"
                      value={
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {(active.services || []).map((s) => (
                            <Badge key={s} variant="outline" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}>{s}</Badge>
                          ))}
                        </div>
                      }
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Detail label="Notes" value={active.notes || "—"} />
                  </div>
                </div>

                <BillingPanel lead={active} onChange={handleLeadUpdate} />
              </div>

              {/* Sticky footer */}
              <div
                className="shrink-0 border-t bg-white px-5 sm:px-7 py-3"
                style={{ borderColor: "#E2E8F0" }}
                data-testid="lead-detail-footer"
              >
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap" data-testid="consumer-record-actions">
                    {active.is_binned ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          style={{ borderColor: "rgba(212,175,55,0.5)", background: "rgba(212,175,55,0.12)", color: "#7A5E12" }}
                          onClick={() => onRestoreFromBin(active.id)}
                          data-testid="consumer-action-unbin"
                        >
                          <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                          Restore from Bin
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 text-xs"
                          style={{ color: "#B91C1C" }}
                          onClick={() => onPermanentDelete(active.id)}
                          data-testid="consumer-action-delete"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          Permanently delete
                        </Button>
                      </>
                    ) : active.is_archived ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          style={{ borderColor: "rgba(212,175,55,0.5)", background: "rgba(212,175,55,0.12)", color: "#7A5E12" }}
                          onClick={() => onRestore(active.id)}
                          data-testid="consumer-action-restore"
                        >
                          <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
                          Restore from Archive
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          style={{ borderColor: "#E2E8F0", color: "#B91C1C" }}
                          onClick={() => onMoveToBin(active.id)}
                          data-testid="consumer-action-bin"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          Move to Bin
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                          onClick={() => onArchive(active.id)}
                          data-testid="consumer-action-archive"
                        >
                          <Archive className="h-3.5 w-3.5 mr-1.5" />
                          Archive
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          style={{ borderColor: "#E2E8F0", color: "#B91C1C" }}
                          onClick={() => onMoveToBin(active.id)}
                          data-testid="consumer-action-bin"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          Move to Bin
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    <StatusDropdown current={active.status} onSelect={(s) => onChangeStatus(active.id, s)} />
                    {!active.is_archived && !active.is_binned ? (
                      <SendPaymentButton lead={active} onChange={handleLeadUpdate} size="sm" />
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value, accent, testId }) => (
  <div className="rounded-xl border bg-white px-5 py-4" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
    <div className="text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#475569" }}>{label}</div>
    <div className="mt-2 flex items-end gap-3">
      <div className="font-display text-3xl font-bold" style={{ color: "#0B2545" }}>{value}</div>
      <div className="h-1.5 w-8 rounded-full" style={{ background: accent }} />
    </div>
  </div>
);

const UrgencyPill = ({ value }) => {
  const c = URGENCY_COLOR[value] || URGENCY_COLOR.Standard;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: c.bg, color: c.color }}>
      {value}
    </span>
  );
};

const StatusDropdown = ({ current, onSelect }) => {
  const c = STATUS_COLOR[current] || STATUS_COLOR.new;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-all"
          style={{ background: c.bg, color: c.color, borderColor: c.border }}
          data-testid={`status-trigger-${current}`}
        >
          {current}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {STATUSES.map((s) => (
          <DropdownMenuItem key={s} onClick={() => onSelect(s)} className="capitalize" data-testid={`status-option-${s}`}>{s}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-medium" style={{ color: "#475569" }}>{label}</div>
    <div className="mt-1 text-sm" style={{ color: "#0F172A" }}>{value}</div>
  </div>
);
